<template>
    <ds-modal
        :is-open="isOpen"
        data-qa="send-test-email-modal"
        class="send-test-email-modal"
        show-close
        :title="$t('unlayerBuilder.sendTestEmail.modal.title')"
        @close="$emit('close')"
    >
        <template #header>
            <ds-filled-button
                :loading="isSending || loadingContentRisk"
                aria-label="Send test email"
                data-qa="test-email-modal-send-button"
                @click="sendTestEmail"
            >
                {{ $t('unlayerBuilder.sendTestEmail.sendButton.label') }}
            </ds-filled-button>
        </template>

        <template #default>
            <ContentRiskMessages
                v-if="showContentRiskMessages"
                :messages="contentRiskMessages"
                :quality="contentRiskQuality"
                class="content-risk-messages"
                dismissable
            >
                <strong>
                    {{ $tc(contentRiskTitleBold, contentRiskMessages.length, { messages: contentRiskMessages.length }) }}
                </strong>
                {{ $t(contentRiskTitle) }}
            </ContentRiskMessages>
            <DkimVerificationAlert
                v-show="dkimWarning"
                :show-test-email-sender-warning="dkimWarning"
                class="dkim-verification-alert"
                new-dkim-setup-enabled
                :show-verified-domains-new-keys-only-enabled="showVerifiedDomainsNewKeysOnlyEnabled"
                dmarc-verification-enabled
                @error-loading-domains="$emit('email.broadcast.editor.errors.fetchingCustomDomains')"
            />

            <form
                novalidate
            >
                <ds-multiselect
                    v-model="selectedSender"
                    help-text
                    help-text-prop="email"
                    label-prop="name"
                    searchable
                    :submitted="submitted"
                    class="sender-multiselect"
                    allow-add
                    value-prop="label"
                    :force-invalid="unauthenticatedSenderError"
                    :loading="loadingUsers"
                    :options="usersWithNames"
                    :placeholder="$t('email.broadcast.test.sender.fromInputPlaceholder')"
                    :add-text="$t('email.broadcast.steps.sender.customizeEmail')"
                    @add="handleAddCustomEmail"
                    @input="validateFromAddress"
                >
                    <template #help>
                        {{ unauthenticatedSenderError ? $t('email.broadcast.test.sender.unauthenticated') : '' }}
                    </template>
                </ds-multiselect>

                <ds-multiselect
                    id="send-test-email-modal-multiselect"
                    v-model="selectedOptions"
                    required
                    multiple
                    searchable
                    :placeholder="$t('unlayerBuilder.sendTestEmail.recipientInput.placeholder')"
                    help-text
                    help-text-prop="email"
                    allow-add
                    :add-text="$t('unlayerBuilder.sendTestEmail.dropdownAddText')"
                    :force-invalid="error"
                    :limit="$options.MAX_RECIPIENTS"
                    :data-qa="'recipient-search-multiselect'"
                    :class="{ 'recipient-additional-margin': (!helpTextCritical && helpText) }"
                    :loading="loadingUsers || isSearchInProgress"
                    :options="options"
                    :submitted="submitted"
                    @updateSearch="handleUpdateSearch"
                    @blur="handleRecipientSelectionEnd"
                    @add="openCustomRecipientModal"
                >
                    <template #selection="{ option, remove }">
                        <ds-chip
                            remove
                            with-interaction
                            class="option-icon"
                            @mousedown.native.stop.prevent="remove"
                        >
                            <template #leading>
                                <ds-icon :name="option.recipientType === $options.RECIPIENT_TYPE.CUSTOM ? 'email' : 'user'" />
                            </template>

                            <template #default>
                                {{ currentUser.email === option.email ? $t("email.broadcast.test.recipient.selectedUserIsCurrentUser", {name: option.fullName, email: option.email}) : option.label }}
                            </template>
                        </ds-chip>
                    </template>

                    <template #help>
                        <span
                            :key="helpText"
                            class="help-text"
                            :class="{ 'help-text-critical': !!helpTextCritical }"
                        >
                            {{ helpText }}
                        </span>
                    </template>
                </ds-multiselect>

                <ContactForResolvingMergeFields
                    v-if="contactSearchEnabled"
                    @selected="(c) => contactForResolvingMergeFields = c"
                />

                <DsTextAreaField
                    v-model="emailInstructions"
                    type="text"
                    data-qa="send-test-email-modal-textarea"
                    name="emailAdditionalInstructions"
                    :label="$t('email.broadcast.sendTestEmail.modal.instructions.label')"
                >
                    <template #help>
                        <span class="help-text">
                            {{ $t('email.broadcast.sendtestEmail.modal.help') }}
                        </span>
                    </template>
                </DsTextareafield>
            </form>

            <EmailBroadcastValidatedDomainsModal
                data-qa="email-broadcast-validated-domains-modal"
                is-test-email
                :is-open="isValidatedDomainsModalOpen"
                :domains="validatedDomains"
                :show-verified-domains-new-keys-only-enabled="showVerifiedDomainsNewKeysOnlyEnabled"
                @custom-sender-selected="handleCustomEmailSelected"
                @close="handleValidatedDomainsModalClose"
            />

            <CustomRecipientModal
                :is-open="showCustomRecipientModal"
                @back="showCustomRecipientModal = false"
                @recipient-added="saveCustomRecipient"
            />
        </template>
    </ds-modal>
</template>

<script>
import debounce from 'lodash.debounce';
import { fetchActiveUsers, getCustomDomains } from '@/api/broadcasts/emailBroadcast';
import CustomRecipientModal from './CustomRecipientModal';
import { getCurrentUser, sendTestEmailMultiple } from '@/api/campaigns/campaignUnlayerEmail';
import { openSearch } from '@/api/shared';
import { TEST_EMAIL_MAX_RECIPIENTS as MAX_RECIPIENTS } from '@/components/broadcast/constants/emailBroadcast.constants';
import { SEARCHING_DEBOUNCE_DELAY, INPUT_DEBOUNCE_DELAY } from '@/components/shared/constants/timing.constants';
import { getEmailLabel } from '@/utils/email.util';
import {
  getEmailLabel as getEmailSendValue, getFromAddress, getEmailName, sortPeopleByFullNameAndEmail,
} from '@/components/broadcast/utils/emailBroadcast.util';
import {
    getVerifiedDomainsKeysOrDmarc,
    getVerifiedDomainsNewKeys,
} from '@/components/settings/util/domains.util';
import EmailBroadcastValidatedDomainsModal
from '@/components/broadcast/email/EmailBroadcastValidatedDomainsModal.vue';
import DkimVerificationAlert from '@/components/shared/DkimVerificationAlert.vue';
import ContentRiskMessages from '../ContentRiskMessages.vue';
import ContactForResolvingMergeFields from './ContactForResolvingMergeFields.vue';
import { CONTENT_RISK_QUALITY } from '../content-risk.constants';

const RECIPIENT_TYPE = {
    APP_USER: 'APP_USER',
    CONTACT: 'CONTACT',
    CUSTOM: 'CUSTOM',
};

export const SUBJECT_PREFIX = '[Preview]';
export const DEFAULT_SUBJECT = `${SUBJECT_PREFIX} no subject`;
export const EMPTY_SENDER = {
    email: '',
    name: '',
};
export const CONTACT_OWNER_FROM_ADDRESS = '"~Owner.FirstName~ ~Owner.LastName~" <~Owner.Email~>';

const getRecipientOption = ({
    id,
    fullName,
    email,
    recipientType,
}) => {
    return {
        id,
        recipientType,
        value: `${recipientType}-${id}`,
        fullName,
        email,
        label: getEmailLabel({ fullName, email }),
    };
};

export default {
    name: 'SendTestEmailModal',
    components: {
        DkimVerificationAlert,
        CustomRecipientModal,
        ContentRiskMessages,
        EmailBroadcastValidatedDomainsModal,
        ContactForResolvingMergeFields,
    },

    MAX_RECIPIENTS,
    RECIPIENT_TYPE,

    props: {
        funnelId: {
            type: String,
            default: null,
        },
        fromAddress: String,
        htmlBody: String,
        subject: String,
        previewText: String,
        isOpen: Boolean,
        showVerifiedDomainsNewKeysOnlyEnabled: Boolean,
        contentRiskQuality: String,
        contentRiskMessages: {
            type: Array,
            default: () => [],
        },
        fetchContentRisks: {
            type: Function,
            default: async (x) => x,
        },
        loadingContentRisk: Boolean,
        contactSearchEnabled: Boolean,
    },

    data() {
        return {
            loadingUsers: false,
            selectedOptions: [],
            selectedSender: EMPTY_SENDER,
            unauthenticatedSenderError: false,
            broadcastFromAddress: '',
            validatedDomains: [],
            submitted: false,
            isSending: false,
            error: false,
            currentUser: {},
            users: [],
            contacts: [],
            searchText: '',
            isSearchInProgress: false,
            didSelectOptionAtleastOnce: false,
            showSearchHelpText: false,
            isValidatedDomainsModalOpen: false,
            showCustomRecipientModal: false,
            emailInstructions: '',
            contactForResolvingMergeFields: null,
        };
    },

    created() {
        this.handleContentChange = debounce(this.handleContentChange, INPUT_DEBOUNCE_DELAY, { leading: true });

        this.load();

        this.searchForContacts = debounce(this.searchForContacts, SEARCHING_DEBOUNCE_DELAY);
    },

    watch: {
        isOpen(value) {
            if (value) {
                this.reset();
            }
        },

        selectedOptions(val) {
            if (val.length > 0) {
                this.error = false;
                this.didSelectOptionAtleastOnce = true;
            }
        },

        emailInstructions() {
            this.handleContentChange();
        },
    },

    computed: {
        hasMaxRecipientsLimitReached() {
            return this.selectedOptions.length >= MAX_RECIPIENTS;
        },

        usersWithNames() {
            return [
                ...this.users.map(({ id, fullName, email }) => ({
                    email,
                    id,
                    label: getEmailLabel({ email, fullName }),
                    name: fullName,
                    value: getEmailSendValue({ email, fullName }),
                })),
            ];
        },

        verifiedEmailDomainOptions() {
            return this.validatedDomains.map((domain) => ({
                label: `@${domain}`,
                value: `@${domain}`,
            }));
        },

        options() {
            const appUsers = this.getFormattedOptions(RECIPIENT_TYPE.APP_USER);

            const contacts = this.getFormattedOptions(RECIPIENT_TYPE.CONTACT);

            return [
                {
                    label: this.$t('unlayerBuilder.sendTestEmail.helpText.dropdownGroup.appUsers'),
                    options: appUsers,
                },
                {
                    label: this.$t('unlayerBuilder.sendTestEmail.helpText.dropdownGroup.contacts'),
                    options: contacts,
                    showLabelWhenEmpty: (this.searchText.length >= 2),
                },
            ];
        },

        helpTextCritical() {
            if (this.error && this.selectedOptions.length === 0) {
                return this.$t('unlayerBuilder.sendTestEmail.error.noRecipients');
            }

            if (this.hasMaxRecipientsLimitReached) {
                return this.$t('unlayerBuilder.sendTestEmail.error.maxRecipientLimit');
            }

            return '';
        },

        helpText() {
            // order matters for these conditions
            if (this.helpTextCritical) {
                return this.helpTextCritical;
            }

            if (this.showSearchHelpText) {
                return this.$t('unlayerBuilder.sendTestEmail.helpText.searchTip');
            }

            return '';
        },

        dkimWarning() {
            return this.verifiedEmailDomainOptions.length < 1;
        },

        contentRiskTitleBold() {
            return this.contentRiskQuality === CONTENT_RISK_QUALITY.SPAM_NOT_SENDABLE
                ? 'unlayerBuilder.sendTestEmail.inlineAlert.highRiskIssues'
                : 'unlayerBuilder.sendTestEmail.inlineAlert.moderateRiskIssues';
        },

        contentRiskTitle() {
            return this.contentRiskQuality === CONTENT_RISK_QUALITY.SPAM_NOT_SENDABLE
                ? 'unlayerBuilder.sendTestEmail.inlineAlert.highRiskInstruction'
                : 'unlayerBuilder.sendTestEmail.inlineAlert.moderateRiskInstruction';
        },

        hasInstructions() {
            return (this.emailInstructions.length > 0);
        },

        showContentRiskMessages() {
            return (this.hasInstructions
                    && this.contentRiskMessages.length > 0
                    && this.contentRiskQuality !== CONTENT_RISK_QUALITY.OK
                    && !this.loadingContentRisk
            );
        },
    },

    methods: {
        async load() {
            const promises = [
                this.getCurrentUser(),
                this.getActiveUsers(),
                this.fetchValidatedCustomDomains(),
            ];

            await Promise.all(promises);

            this.selectedSender = this.initializeSelectedSender();
            this.validateFromAddress();
        },

        async getCurrentUser() {
            try {
                this.currentUser = await getCurrentUser();
            } catch (error) {
                this.$error({ message: 'Something went wrong while loading your current app user. Please try again.' });
            }
        },

        async getActiveUsers() {
            this.loadingUsers = true;

            try {
                this.users = await fetchActiveUsers();
                this.loadingUsers = false;
            } catch (e) {
                this.$error({ message: "Something went wrong while loading your app's users. Please try again." });
                this.loadingUsers = false;
            }
        },

        getFormattedOptions(recipientType) {
            return (recipientType === RECIPIENT_TYPE.APP_USER ? this.users : this.contacts)
                .filter(({ email }) => !!email)
                .map(({ id, fullName, email }) => getRecipientOption({
                    id,
                    fullName,
                    email,
                    recipientType,
                }));
        },

        async fetchValidatedCustomDomains() {
            try {
                const customDomains = await getCustomDomains();

                if (!this.showVerifiedDomainsNewKeysOnlyEnabled) {
                    this.validatedDomains = getVerifiedDomainsKeysOrDmarc(customDomains).map(
                        (domain) => {
                            return {
                                label: `@${domain}`,
                                value: `@${domain}`,
                            };
                        },
                    );

                    return;
                }

                this.validatedDomains = getVerifiedDomainsNewKeys(customDomains).map((domain) => {
                    return {
                        label: `@${domain}`,
                        value: `@${domain}`,
                    };
                });
            } catch (e) {
                this.$emit('error', this.$t('email.broadcast.editor.errors.fetchingCustomDomains'));
            }
        },

        initializeSelectedSender() {
            const { email, fullName } = this.currentUser;
            const defaultFromAddress = {
                email,
                label: getEmailLabel({ email, fullName }),
                name: fullName,
                value: getEmailSendValue({ email, fullName }),
            };

            // This should not be reactive, otherwise the broadcast sender could be changed if we're not careful
            if (!this.broadcastFromAddress && this.fromAddress) {
                this.broadcastFromAddress = this.fromAddress;
            } else {
                return defaultFromAddress;
            }
            const { broadcastFromAddress } = this;

            if (broadcastFromAddress === CONTACT_OWNER_FROM_ADDRESS) {
                return defaultFromAddress;
            }
            const broadcastFromAddressFormatted = getFromAddress(broadcastFromAddress).toLowerCase();
            const broadcastEmailName = getEmailName(broadcastFromAddress).toLowerCase();
            let testFrom = this.usersWithNames
                .find((user) => user.email.toLowerCase() === broadcastFromAddressFormatted
                    && user.name.toLowerCase() === broadcastEmailName);

            if (!testFrom) {
                const isBroadcastFromDomainVerified = this.verifiedEmailDomainOptions
                    .some(({ value }) => broadcastFromAddress.includes(value));

                if (isBroadcastFromDomainVerified) {
                    testFrom = {
                        email: getFromAddress(broadcastFromAddress),
                        name: getEmailName(broadcastFromAddress),
                        value: broadcastFromAddress,
                    };
                }
            }

            if (!testFrom) {
                testFrom = this.usersWithNames
                    .find((user) => user.email.toLowerCase() === broadcastFromAddressFormatted);
            }

            if (!testFrom) {
                testFrom = this.usersWithNames
                    .find((user) => user.name.toLowerCase() === broadcastEmailName);
            }

            if (!testFrom) {
                return defaultFromAddress;
            }

            return testFrom;
        },

        handleCustomEmailSelected(customSender) {
            this.selectedSender = {
                name: customSender.displayName,
                email: customSender.emailAddress,
                value: getEmailSendValue({ email: customSender.emailAddress, fullName: customSender.displayName }),
            };
            this.validateFromAddress();
        },

        validateFromAddress() {
            const fromDomain = this.selectedSender.email
                .split('@')
                .pop()
                .toLowerCase();
            const isValid = this.validatedDomains.some((domain) => {
                return domain
                    ? domain.value.toLowerCase().slice(1, domain.length) === fromDomain
                    : false;
            });

            if (!isValid) {
                this.unauthenticatedSenderError = true;
            } else {
                this.unauthenticatedSenderError = false;
            }
        },

        async sendTestEmail() {
            this.submitted = true;

            if (this.selectedOptions.length === 0) {
                this.error = true;
            } else {
                this.error = false;
            }

            if (this.unauthenticatedSenderError) {
                this.submitted = false;

                return;
            }

            if (this.hasInstructions && this.contentRiskQuality === CONTENT_RISK_QUALITY.SPAM_NOT_SENDABLE) {
                return;
            }

            if (this.error) {
                this.submitted = false;

                return;
            }

            const emailList = [...new Set(this.selectedOptions.map(({ email }) => email))];

            this.isSending = true;

            const selectedFromAddress = this.selectedSender.value;


            let htmlBodyContent = this.htmlBody;

            if (this.hasInstructions) {
                const preInstructions = '<div>';
                const postInstructions = '<br></div>';
                const intermediateInstructions = preInstructions.concat(this.emailInstructions).concat(postInstructions);

                htmlBodyContent = intermediateInstructions.concat(this.htmlBody)
            }

            const testEmailPayload = {
                subject: this.subject ? `${SUBJECT_PREFIX} ${this.subject}` : DEFAULT_SUBJECT,
                html: htmlBodyContent,
                previewText: this.previewText,
                fromAddress: selectedFromAddress,
                emailList,
                funnelId: Number(this.funnelId) || null,
                contactId: Number(this.contactForResolvingMergeFields?.id) || null,
            };

            try {
                await sendTestEmailMultiple(testEmailPayload);

                this.isSending = false;
                this.submitted = false;

                this.$toast({ message: 'Your test email has been sent.' });
                this.$emit('close');
            } catch (error) {
                this.isSending = false;

                this.$error({ message: 'Something went wrong while sending your test email. Please try again.' });
            }
        },

        handleUpdateSearch(s) {
            this.searchText = s;
            this.searchForContacts();
        },

        async searchForContacts() {
            if (this.searchText.length < 2) return;

            this.isSearchInProgress = true;

            try {
                const { queryResults } = await openSearch({
                    documentTypes: 'contact',
                    term: this.searchText,
                    highlightEnabled: false,
                });

                this.contacts = queryResults
                    .map(({ data: { email, fullName }, id }) => ({
                        email,
                        fullName,
                        id,
                        label: getEmailLabel({ fullName, email }),
                        recipientType: RECIPIENT_TYPE.CONTACT,
                    }))
                    .sort(sortPeopleByFullNameAndEmail);
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error('Failed to search for contacts:', e);
            } finally {
                this.isSearchInProgress = false;
            }
        },

        handleRecipientSelectionEnd() {
            this.showSearchHelpText = !this.didSelectOptionAtleastOnce;
        },

        handleAddCustomEmail() {
            this.isValidatedDomainsModalOpen = true;
        },
        handleValidatedDomainsModalClose() {
            this.isValidatedDomainsModalOpen = false;
        },

        openCustomRecipientModal() {
            if (this.hasMaxRecipientsLimitReached) return;

            this.showCustomRecipientModal = true;
        },

        async saveCustomRecipient({ name, email }) {
            this.selectedOptions.push(getRecipientOption({
                id: Date.now(),
                fullName: name,
                email,
                recipientType: RECIPIENT_TYPE.CUSTOM,
            }));

            this.showCustomRecipientModal = false;

            await this.$nextTick();
            this.handleRecipientSelectionEnd();
        },

        reset() {
            this.submitted = false;
            this.isSending = false;
            this.selectedOptions = [];
        },

        handleContentChange() {
            this.fetchContentRisks(this.emailInstructions);
        },
    },
};
</script>

<style lang="scss" scoped>
.send-test-email-modal {
    :deep(.modal-content-wrapper) {
        border-radius: $spacing-150;
    }

    :deep(.modal-header) {
        border-radius: $spacing-150 $spacing-150 0 0;
    }

    :deep(input[type=search]) {
        box-sizing: border-box;
    }

    --input-margin-bottom: #{$spacing-250};
    --modal-overflow: visible;
}

.help-text {
    position: absolute;
}

.help-text-critical {
    color: $color-red;
    position: relative;
}

.option-icon {
    padding-left: $spacing-100;
}

.dkim-verification-alert {
    margin-bottom: $spacing-250;
}

.recipient-additional-margin {
    margin-bottom: $spacing-400;
}

.content-risk-messages {
    margin-bottom: $spacing-200;
}
</style>
